import { useState } from "react";
import {
  Autocomplete,
  Avatar,
  Button,
  Box,
  Divider,
  IconButton,
  Menu,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Delete from "@mui/icons-material/Delete";
import MoreVert from "@mui/icons-material/MoreVert";
import { getEquipmentType } from "../equipmentTypes";
import NodeAnexos from "../nodeAnexos";
import HostAutocomplete from "./HostAutocomplete";
import HostLinkSelector from "./HostLinkSelector";
import styles from "./styles";

export default function NodeInspector({
  selectedNode = null,
  editMode = false,
  isExpanded = false,
  updateNodeData = () => {},
  deleteSelectedNode = () => {},
  equipmentTypes = [],
  nodeAnexos = { saved: [], newFiles: [], deletedIds: [], loaded: false, loading: false },
  onAddNodeAnexoFiles = () => {},
  onRemoveNodeAnexoFile = () => {},
  onDownloadNodeAnexoFile = () => {},
  hostOptions = [],
  hostStatusMap = {},
}) {
  const [nodeMenuAnchor, setNodeMenuAnchor] = useState(null);
  const theme = useTheme();
  const nodeStyles = styles(theme);

  // Identifica o tipo atual (fixo ou personalizado) para exibir o titulo do inspector
  const currentType = getEquipmentType(selectedNode?.data?.tipo, equipmentTypes);

  // Valor atual selecionado como objeto para o Autocomplete
  const valorAtual = (equipmentTypes || []).find(
    (t) => t?.id === selectedNode?.data?.tipo,
  ) || null;

  // Abre o menu de acoes sem propagar o clique para o React Flow
  const handleOpenNodeMenu = (evento) => {
    evento?.stopPropagation?.();
    setNodeMenuAnchor(evento?.currentTarget || null);
  };

  // Fecha o menu de acoes do node
  const handleCloseNodeMenu = (evento) => {
    evento?.stopPropagation?.();
    setNodeMenuAnchor(null);
  };

  // Atualiza tipo selecionado e fecha o menu
  const handleTypeChange = (tipo) => {
    updateNodeData?.("tipo", tipo?.id);
    setNodeMenuAnchor(null);
  };

  // Callback do Autocomplete ao selecionar um tipo de equipamento
  const handleTypeSelect = (_evento, tipo) => {
    if (tipo) handleTypeChange(tipo);
  };

  // Exclui node selecionado e fecha o menu
  const handleDeleteNode = () => {
    setNodeMenuAnchor(null);
    deleteSelectedNode?.();
  };

  // Troca o host vinculado e limpa as interfaces selecionadas (pertenciam ao host anterior).
  // As duas chamadas caem na mesma sessão de histórico (ver nodeEditSnapshotRef em
  // diagramaEquipamentos/index.js), então viram um único passo de desfazer
  const handleHostChange = (novoHostId) => {
    updateNodeData?.("hostId", novoHostId);
    if (selectedNode?.data?.interfaces?.length) updateNodeData?.("interfaces", []);
  };

  return (
    <Box
      sx={{
        ...nodeStyles.inspectorBox,
        ...(isExpanded ? nodeStyles.inspectorBoxExpanded : {}),
      }}
    >
      <Box sx={nodeStyles.inspectorHeader}>
        <Typography sx={nodeStyles.inspectorTitle}>
          {currentType?.label || "Equipamento"}
        </Typography>
        <Tooltip title="Mais acoes" arrow>
          <span>
            <IconButton
              size="small"
              aria-label="Mais ações"
              onClick={handleOpenNodeMenu}
              disabled={!editMode}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={nodeMenuAnchor}
        open={Boolean(nodeMenuAnchor)}
        onClose={handleCloseNodeMenu}
        onClick={(evento) => evento?.stopPropagation?.()}
        slotProps={{ paper: { sx: nodeStyles.nodeMenu } }}
      >
        <Box sx={nodeStyles.nodeMenuContent}>
          {/* Seletor de tipo de equipamento com busca por digitação */}
          <Box onKeyDown={(e) => e.stopPropagation()}>
            <Autocomplete
              size="small"
              fullWidth
              options={(equipmentTypes || [])}
              getOptionLabel={(tipo) => tipo?.label || ""}
              isOptionEqualToValue={(opcao, valor) => opcao?.id === valor?.id}
              value={valorAtual}
              onChange={handleTypeSelect}
              noOptionsText="Nenhum tipo encontrado"
              ListboxProps={{ sx: nodeStyles.autocompleteListbox }}
              renderOption={(props, tipo) => {
                // Mesmo padrão de ícone pra todos os tipos (não só personalizados): avatar com
                // o ícone customizado, ou o ícone fixo do catálogo (mesma fonte que PaletaEquipamentos)
                const Icone = tipo?.icon;
                return (
                  <Box component="li" {...props} key={tipo?.id} sx={nodeStyles.autocompleteOption}>
                    {tipo?.isCustom ? (
                      <Avatar
                        src={tipo?.iconUrl}
                        variant="rounded"
                        sx={nodeStyles.customTypeOptionAvatar}
                      />
                    ) : Icone ? (
                      <Icone sx={nodeStyles.autocompleteOptionIcon} />
                    ) : null}
                    {tipo?.label}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo de equipamento"
                  aria-label="Tipo de equipamento"
                />
              )}
            />
          </Box>
          <Divider />
          <Button
            color="error"
            size="small"
            fullWidth
            startIcon={<Delete fontSize="small" />}
            onClick={handleDeleteNode}
            sx={nodeStyles.deleteButton}
          >
            Excluir
          </Button>
        </Box>
      </Menu>
      <HostAutocomplete
        value={selectedNode?.data?.nome || ""}
        onChange={(novoValor) => updateNodeData?.("nome", novoValor)}
        hostOptions={hostOptions}
        disabled={!editMode}
      />
      <HostLinkSelector
        hostId={selectedNode?.data?.hostId || ""}
        interfaceValues={selectedNode?.data?.interfaces || []}
        onChangeHost={handleHostChange}
        onChangeInterface={(novoValor) => updateNodeData?.("interfaces", novoValor)}
        hostOptions={hostOptions}
        hostStatus={hostStatusMap?.[selectedNode?.data?.hostId]}
        disabled={!editMode}
      />
      <TextField
        label="Observações"
        size="small"
        fullWidth
        multiline
        minRows={3}
        maxRows={14}
        autoComplete="off"
        sx={nodeStyles.observationsField}
        value={selectedNode?.data?.observacoes || ""}
        onChange={(evento) =>
          updateNodeData?.("observacoes", evento?.target?.value)
        }
        disabled={!editMode}
      />
      <Divider sx={nodeStyles.anexosDivider} />
      <Typography sx={nodeStyles.inspectorTitle}>Anexos</Typography>
      <NodeAnexos
        nodeAnexos={nodeAnexos}
        editMode={editMode}
        inputId={`input-upload-node-${selectedNode?.id}`}
        onAddFiles={onAddNodeAnexoFiles}
        onRemoveFile={onRemoveNodeAnexoFile}
        onDownloadFile={onDownloadNodeAnexoFile}
      />
    </Box>
  );
}
